/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  CellClickedEvent,
  ColDef,
  Column,
  GridApi,
  GridState,
  IRowNode,
  RowClickedEvent,
} from '@ag-grid-community/core';
import { userRoles } from '@constants/common';
import { FeatureFlags } from '@constants/featureFlags';
import { datadogRum } from '@datadog/browser-rum';
import {
  ILoadBoardViewModel,
  SavedSearchCategory,
  SavedSearchT,
  autoHeightColumns,
  columnDefs,
  columnDefsMap,
  fetchLoadBoardDetails,
  loadBoardStore,
  nonClickableColumns,
  saveLoadboardCache,
  setFilteredItems,
} from '@store/loadboard';
import { MASS_SHARE_CAPACITY_SELECT_FIELD } from '@store/loadboard/constants';
import { fetchSavedSearches, saveFilter } from '@store/loadboard/services';
import {
  relativeDatesForGrid,
  relativeDatesForSave,
} from '@store/loadboard/utils';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { userStore } from '@store/user';
import { getSortModelFromColumnState } from '@utils/utils';
import { debounce, isEqual } from 'lodash';
import { createMemo, createSignal } from 'solid-js';
import { effect } from 'solid-js/web';

import { Grid } from './Grid';
import { filterRulesByValue } from './components/constants';

export interface gridStateWithAdditionalProps extends GridState {
  quickSearch: string;
  customFilter: string;
  myItems?: boolean;
}

type GridEventT = {
  source: string;
  column: Column;
  type: string;
};

type PropsT = {
  items: Record<string, unknown>[];
  oldPicks: ILoadBoardViewModel[];
  oldDrops: ILoadBoardViewModel[];
  loading: boolean;
  onUpdateItems: (newItem: string, col: string, loadId: number) => void;
  onUpdatePostings: (postings: ILoadBoardViewModel[]) => void;
  onDataFetched: (gridApi: GridApi) => void;
  updatePicksAndDrops?: () => void;
  onRowClicked?:
    | ((e: RowClickedEvent, col?: string | undefined) => void)
    | undefined;
  onRefreshGrid?: () => void;
  onRefreshLoadboardRow?: (orderId: number) => Promise<void>;
  isRefreshingLoadboardRow?: () => boolean;
  fetchItemsFn?: () => Promise<ILoadBoardViewModel[]>;
  showOptimized?: boolean;
  setRestrictToMyUserId?: (n: number) => void;
};

//this is the loadboard grid
export const LoadBoardGrid = (props: PropsT) => {
  const [cache, setCache] = createSignal<SavedSearchT>();
  const [customFilterCache, setCustomFilterCache] = createSignal<string>('');
  const [gridReady, setGridReady] = createSignal<boolean>(false);

  const lbColumnDefs = createMemo(() => {
    const defs = columnDefs.map((rec) => {
      let partial: Partial<ColDef> = {};

      if (
        rec.field === 'customerName' &&
        !!userStore.user.roles?.includes(userRoles.CAPACITY)
      ) {
        partial = {
          hide: true,
          lockVisible: true,
          suppressFiltersToolPanel: true,
        };
      }

      return {
        ...rec,
        ...partial,
        onCellClicked: (e: CellClickedEvent) => {
          if (
            (e.colDef.field as unknown as string) &&
            !nonClickableColumns.includes(e.colDef.field as unknown as string)
          ) {
            props.onRowClicked?.(e, e.colDef.field);
          }
        },
      };
    });

    return defs;
  });

  effect(() => {
    const newCache = loadBoardStore.cache;

    if (
      newCache !== undefined &&
      (cache() === undefined || newCache.params !== cache()?.params)
    ) {
      setCache(newCache);
    }
  });

  return (
    <Grid
      gridReady={gridReady()}
      data={props.items}
      onRefreshGrid={props.onRefreshGrid}
      onRefreshLoadboardRow={props.onRefreshLoadboardRow}
      isRefreshingLoadboardRow={props.isRefreshingLoadboardRow}
      columnDefs={lbColumnDefs()}
      oldPicks={props.oldPicks}
      oldDrops={props.oldDrops}
      onUpdateItems={props.onUpdateItems}
      onUpdatePostings={props.onUpdatePostings}
      loading={props.loading}
      customFilterCache={customFilterCache}
      setCustomFilterCache={setCustomFilterCache}
      updatePicksAndDrops={props.updatePicksAndDrops}
      showOptimized={props.showOptimized}
      onGridReady={async (gridApi) => {
        await (async () => {
          gridApi.showLoadingOverlay();
          await userStore.userPromise;
          datadogRum.setUser({
            id: `${userStore.user.id}`,
            name: `${userStore.user.name}`,
          });

          if (!!props.fetchItemsFn) {
            await props.fetchItemsFn();
          } else {
            await fetchLoadBoardDetails();
          }

          props.onDataFetched(gridApi);
          gridApi.hideOverlay();
          setTimeout(() => {
            gridApi.resetRowHeights();
          }, 10);
        })();

        function getFilteredRows() {
          const rows: ILoadBoardViewModel[] = [];

          gridApi.forEachNodeAfterFilterAndSort(
            (row: IRowNode<ILoadBoardViewModel>) => {
              if (row.data === undefined) {
                return;
              }

              rows.push(row.data);
            },
          );

          return rows;
        }

        const saveGridState = async () => {
          const gridState = gridApi.getState();
          const quickSearch = gridApi.getQuickFilter() ?? '';
          const columns = gridApi.getColumnState();

          const stampedFilterModel = relativeDatesForSave(
            gridApi.getFilterModel(),
          );
          const completeState = {
            ...gridState,
            filter: {
              ...(gridState.filter ?? { filterModel: null }),
              filterModel: stampedFilterModel,
            },
            quickSearch,
            customFilter: customFilterCache() || '',
            myItems: loadBoardStore.myItems,
            sort: {
              sortModel: getSortModelFromColumnState(columns),
            },
          };

          try {
            const newCache = await saveFilter(
              completeState as unknown as Record<string, unknown>,
              SavedSearchCategory.Cache,
              'cache',
            );

            const items = getFilteredRows();
            setFilteredItems(items);

            const savedSearchesFromSession =
              sessionStorage.getItem('savedSearches');

            let savedSearches: SavedSearchT[];

            if (Boolean(savedSearchesFromSession)) {
              savedSearches = JSON.parse(
                savedSearchesFromSession as string,
              ) as SavedSearchT[];
            } else {
              savedSearches = await fetchSavedSearches();
            }

            savedSearches = savedSearches.map((s) => {
              if (s.category === SavedSearchCategory.Cache) {
                return newCache;
              }

              return s;
            });

            sessionStorage.setItem(
              'savedSearches',
              JSON.stringify(savedSearches),
            );

            saveLoadboardCache(newCache);
          } catch (e) {
            // The error is already handled by the `saveFilter` function.
          }
        };

        const onGridStateChanged = async (e: GridEventT) => {
          if (e.source === 'uiColumnDragged') {
            return;
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          } else if (
            (e.source === 'uiColumnResized' &&
              autoHeightColumns.includes(
                // @ts-expect-error test
                e.column.colId as unknown as string,
              )) ||
            e.type == 'columnEverythingChanged'
          ) {
            gridApi.resetRowHeights();
          }

          await saveGridState();
        };

        const shortDebounceHandler = debounce(onGridStateChanged, 150);
        const longDebounceHandler = debounce(shortDebounceHandler, 400);

        const newCache = loadBoardStore.cache;
        gridApi.closeToolPanel();

        gridApi.addEventListener('rowDataUpdated', () => {
          props.updatePicksAndDrops?.();
        });

        if (newCache !== undefined) {
          const gridState = JSON.parse(
            newCache.params,
          ) as gridStateWithAdditionalProps;

          if (gridState.rowGroup !== undefined) {
            gridApi.setRowGroupColumns(gridState.rowGroup.groupColIds);
          }

          setCustomFilterCache(gridState.customFilter);
          gridApi.setGridOption('quickFilterText', gridState.quickSearch);

          if (gridState.columnPinning !== undefined) {
            gridState.columnPinning.leftColIds.forEach((col) => {
              gridApi.setColumnPinned(col, 'left');
            });
            gridState.columnPinning.rightColIds.forEach((col) => {
              gridApi.setColumnPinned(col, 'right');
            });
          }

          if (gridState.filter !== undefined) {
            const filterModel = gridState.filter.filterModel ?? null;
            const advancedFilterModel =
              gridState.filter.advancedFilterModel ?? null;
            const hasColumnFilters =
              filterModel != null && Object.keys(filterModel).length > 0;

            if (
              hasColumnFilters &&
              !!gridState.customFilter &&
              gridState.customFilter in filterRulesByValue
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              const filter = filterRulesByValue[gridState.customFilter]?.[0];

              // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-unnecessary-condition
              if (!!filter) {
                // The dates that we use in any pick or drop date filter are static. So when we save the cache and pull
                // it back down, it should re-apply the pick or drop date filter to ensure that we're showing the right
                // dates.
                filterModel[filter.label] = filter.getFilter();
              }
            }

            if (hasColumnFilters) {
              gridApi.setFilterModel(relativeDatesForGrid(filterModel));
            } else if (advancedFilterModel) {
              gridApi.setAdvancedFilterModel(advancedFilterModel);
            } else {
              gridApi.setFilterModel(null);
            }
          }

          //If user is in a group and postingId is not included in the order colId due to preexisting cache, add it to the front of the array
          if (
            !Boolean(
              gridState.columnOrder?.orderedColIds.includes('postingId'),
            ) &&
            Boolean(userStore.user.postingGroupId)
          ) {
            gridState.columnOrder?.orderedColIds.unshift('postingId');
          }

          if (gridState.sideBar !== undefined) {
            gridApi.setSideBarVisible(gridState.sideBar.visible);
            gridApi.setSideBarPosition(gridState.sideBar.position);
          }

          if (gridState.columnOrder !== undefined) {
            const newColumnDefs = gridState.columnOrder.orderedColIds
              .map((col) => {
                let partial: Partial<ColDef> = {};

                if (
                  (col === 'customerName' &&
                    !!userStore.user.roles?.includes(userRoles.CAPACITY)) ||
                  (col === 'capacityOpsRep' &&
                    !isFeatureFlagEnabled(FeatureFlags.CapacityOpsRepUser)())
                ) {
                  partial = {
                    hide: true,
                    lockVisible: true,
                    suppressFiltersToolPanel: true,
                  };
                }

                return {
                  ...columnDefsMap[col],
                  ...partial,
                };
              })
              // eslint-disable-next-line  @typescript-eslint/no-unnecessary-condition
              .filter((v) => v !== undefined)
              .filter((v) => {
                //If user is not in a group, return all columns except postingId
                if (!Boolean(userStore.user.postingGroupId)) {
                  return v.field !== 'postingId';
                }

                //Otherwise, return all columns
                return v;
              });

            //now merge in ones that are not in the ordered list
            columnDefs.forEach((col) => {
              if (!newColumnDefs.find((def) => def.field === col.field)) {
                newColumnDefs.push(col);
              }
            });
            const columnsWithoutSelection = newColumnDefs.filter(
              (col) => col.field !== MASS_SHARE_CAPACITY_SELECT_FIELD,
            );
            gridApi.setGridOption('columnDefs', columnsWithoutSelection);
          }

          if (gridState.sort !== undefined) {
            gridApi.applyColumnState({
              state: [...gridState.sort.sortModel],
              defaultState: { sort: null },
            });
          }

          if (gridState.columnVisibility !== undefined) {
            const visibility = gridState.columnVisibility.hiddenColIds
              .map((col) => {
                return columnDefs.find((def) => def.field === col);
              })
              .filter((def) => def !== undefined)
              .map((def) => def.field) as string[];

            gridApi.setColumnsVisible(visibility, false);
          }

          if (gridState.columnSizing !== undefined) {
            gridApi.setColumnWidths(
              gridState.columnSizing.columnSizingModel.map((size) => {
                return {
                  key: size.colId,
                  newWidth: size.width !== undefined ? size.width : 100,
                };
              }),
            );
          }
        }

        gridApi.addEventListener('columnPinned', shortDebounceHandler);
        gridApi.addEventListener('filterChanged', (e: GridEventT) => {
          const filters = gridApi.getFilterModel();

          for (const key in filters) {
            if (
              (key === 'pickDate' &&
                customFilterCache().startsWith('pickBy')) ||
              (key === 'dropDate' && customFilterCache().startsWith('dropBy'))
            ) {
              const ogFilter = filters[key];
              const filter =
                filterRulesByValue[customFilterCache()][0]?.getFilter();
              // @ts-expect-error params can exist here.
              delete filter.params;

              if (!isEqual(ogFilter, filter)) {
                setCustomFilterCache('');
              }
            }
          }

          return shortDebounceHandler(e);
        });
        gridApi.addEventListener(
          'columnEverythingChanged',
          shortDebounceHandler,
        );
        gridApi.addEventListener('sortChanged', shortDebounceHandler);
        gridApi.addEventListener('columnMoved', longDebounceHandler);
        gridApi.addEventListener('columnResized', longDebounceHandler);

        gridApi.resetRowHeights();

        // @ts-expect-error test
        window.gridApi = gridApi;
        setGridReady(true);

        const items = getFilteredRows();
        setFilteredItems(items);
      }}
      setRestrictToMyUserId={props.setRestrictToMyUserId}
      onRowClicked={(e, field) => {
        if (
          field !== undefined &&
          !nonClickableColumns.includes(field as unknown as string)
        ) {
          props.onRowClicked?.(e, field);
        }
      }}
      singlePage
      allowFiltering
    />
  );
};
